import { isEqual, range, sortBy, uniq } from 'lodash';
import { TreeNode, TreeNodeType, TreeNodeWithStats } from '../treeNode';
import { TreeWithStats } from './treeHelpers';

export const validateTree = (tree: TreeWithStats, numberOfManifolds: number): TreeStatus => {
  const treeNodes = Object.values(tree);
  const manifoldIndexesPresent = uniq(
    treeNodes
      .map(node => (node.nodeType === TreeNodeType.Manifold ? node.fieldData.manifoldIndex : null))
      .filter(index => index !== null),
  );

  const allManifoldsPresent = isEqual(sortBy(manifoldIndexesPresent), range(numberOfManifolds));

  const nodesWithoutData = treeNodes.some(node => {
    switch (node.nodeType) {
      case TreeNodeType.Manifold:
        return !node.fieldData || node.fieldData.manifoldIndex === null;
      case TreeNodeType.Pump:
      case TreeNodeType.HeatSource:
        return (
          !node.fieldData ||
          hasNullEntries(node.fieldData) ||
          !node.firstChild.exitPipe ||
          hasNullEntries(node.firstChild.exitPipe)
        );
      case TreeNodeType.Junction:
        return (
          !node.firstChild.exitPipe ||
          hasNullEntries(node.firstChild.exitPipe) ||
          !node.secondChild.exitPipe ||
          hasNullEntries(node.secondChild.exitPipe)
        );
    }
  });

  const missingChildren = treeNodes.filter(node => hasMissingChildren(node));
  const incompleteReasons = [];

  if (!allManifoldsPresent) {
    incompleteReasons.push('One or more configured manifolds is missing');
  }

  if (missingChildren.length > 0) {
    incompleteReasons.push('One or more pipe elements has no child element');
  }

  if (nodesWithoutData) {
    incompleteReasons.push('One or more elements have fields which have not been completed');
  }

  return {
    incompleteReasons,
    hasError: treeNodes.some(node => hasError(node)),
  };
};

const hasNullEntries = (object: object) => {
  return Object.values(object).some(fieldValue => fieldValue === null);
};

const hasError = (node: TreeNodeWithStats) => {
  if (node.nodeType === TreeNodeType.Junction) {
    return node.stats.secondChildExitPipe.state === 'Error' || node.stats.firstChildExitPipe.state === 'Error';
  }

  if (node.stats.state === 'Error') {
    return true;
  }

  if (node.nodeType !== TreeNodeType.Manifold) {
    return node.stats.exitPipe.state === 'Error';
  }

  return false;
};

const hasMissingChildren = (node: TreeNode): boolean => {
  switch (node.nodeType) {
    case TreeNodeType.Manifold:
      return false;

    case TreeNodeType.HeatSource:
    case TreeNodeType.Pump:
      return !node.firstChild.id;
    case TreeNodeType.Junction:
      return !node.firstChild.id || !node.secondChild.id;
  }
};

export type TreeStatus = { incompleteReasons: Array<string>; hasError: boolean };
